import { css } from "@emotion/react";
import { Elements, ExpressCheckoutElement } from "@stripe/react-stripe-js";
import type {
  StripeElementsOptions,
  StripeExpressCheckoutElementAvailablePaymentMethodsChangeEvent,
  StripeExpressCheckoutElementOptions,
  StripeExpressCheckoutElementReadyEvent,
} from "@stripe/stripe-js";
import React, {
  createContext,
  useCallback,
  useMemo,
  useRef,
  useState,
} from "react";

import { useStripePromise } from "src/utility/stripe";
import { getWindow } from "src/utility/window";

export enum PaymentRequestReadyStatus {
  /**
   * Hasn't been set up for the first time yet
   */
  UNINITIALIZED,
  /**
   * Ready for paymnets
   */
  READY,
  /**
   * Device doesn't support payments
   */
  UNABLE,
}

export interface PaymentRequestInitializer {
  readyStatus: PaymentRequestReadyStatus;
  isApplePay: boolean;
  /**
   * Mounts the (invisible) wallet availability probe. Any screen that needs
   * to know whether Apple/Google Pay is available before the wallet button
   * itself is on screen (e.g. the donate flow's payment option picker) calls
   * this; it's idempotent and a no-op once the probe is mounted.
   */
  initialize: () => void;
}

const initialState: PaymentRequestInitializer = {
  readyStatus: PaymentRequestReadyStatus.UNINITIALIZED,
  isApplePay: false,
  initialize: () => undefined,
};

export const PaymentRequestContext =
  createContext<PaymentRequestInitializer>(initialState);

/**
 * Only Apple Pay and Google Pay are offered through the Express Checkout
 * Element - the other wallets it supports (Link, PayPal, Amazon Pay, Klarna)
 * either have their own flow here or aren't offered at all.
 */
export const WALLET_ONLY_PAYMENT_METHODS: NonNullable<
  StripeExpressCheckoutElementOptions["paymentMethods"]
> = {
  link: "never",
  paypal: "never",
  amazonPay: "never",
  klarna: "never",
};

/**
 * Express Checkout only reports which wallets are available once an element
 * is mounted, so the probe mounts one off-screen. The amount is a placeholder
 * - it only needs to be a valid non-zero charge for the element to render.
 */
const PROBE_ELEMENTS_OPTIONS: StripeElementsOptions = {
  mode: "payment",
  amount: 100,
  currency: "usd",
  paymentMethodTypes: ["card"],
};

const PROBE_ELEMENT_OPTIONS: StripeExpressCheckoutElementOptions = {
  paymentMethods: WALLET_ONLY_PAYMENT_METHODS,
};

// Not display: none - Stripe still needs to lay the element out to evaluate
// wallet availability, it just must never be visible or clickable.
const probeContainerCss = css`
  position: absolute;
  width: 0;
  height: 0;
  overflow: hidden;
  visibility: hidden;
  pointer-events: none;
`;

interface WalletAvailability {
  applePay: boolean;
  googlePay: boolean;
}

export const PaymentRequestProvider: React.FCC = ({ children }) => {
  const stripePromise = useStripePromise();
  const [probeRequested, setProbeRequested] = useState(false);
  const [availability, setAvailability] = useState<WalletAvailability>();
  const [readyStatus, setReadyStatus] = useState<PaymentRequestReadyStatus>(
    PaymentRequestReadyStatus.UNINITIALIZED
  );

  const initialize = useCallback(() => setProbeRequested(true), []);

  const reportedByChangeEvent = useRef(false);

  const reportAvailability = useCallback(
    ({ applePay, googlePay }: WalletAvailability) => {
      setAvailability({ applePay, googlePay });
      setReadyStatus(
        applePay || googlePay
          ? PaymentRequestReadyStatus.READY
          : PaymentRequestReadyStatus.UNABLE
      );
    },
    []
  );
  const onReady = useCallback(
    ({ availablePaymentMethods }: StripeExpressCheckoutElementReadyEvent) => {
      if (reportedByChangeEvent.current) {
        return;
      }
      reportAvailability({
        applePay: !!availablePaymentMethods?.applePay,
        googlePay: !!availablePaymentMethods?.googlePay,
      });
    },
    [reportAvailability]
  );
  const onAvailablePaymentMethodsChange = useCallback(
    ({
      paymentMethods,
    }: StripeExpressCheckoutElementAvailablePaymentMethodsChangeEvent) => {
      reportedByChangeEvent.current = true;
      reportAvailability({
        applePay: !!paymentMethods?.applePay?.available,
        googlePay: !!paymentMethods?.googlePay?.available,
      });
    },
    [reportAvailability]
  );
  const onLoadError = useCallback(
    () => reportAvailability({ applePay: false, googlePay: false }),
    [reportAvailability]
  );

  // Until the probe reports back, fall back to the same signal the old
  // Payment Request API integration used (Safari exposes ApplePaySession).
  const isApplePay = availability
    ? availability.applePay ||
      (!availability.googlePay && !!getWindow()?.ApplePaySession)
    : !!getWindow()?.ApplePaySession;

  const value = useMemo(
    () => ({ readyStatus, isApplePay, initialize }),
    [readyStatus, isApplePay, initialize]
  );

  return (
    <PaymentRequestContext.Provider value={value}>
      {children}
      {probeRequested && stripePromise && (
        <div css={probeContainerCss} aria-hidden>
          <Elements stripe={stripePromise} options={PROBE_ELEMENTS_OPTIONS}>
            <ExpressCheckoutElement
              options={PROBE_ELEMENT_OPTIONS}
              onReady={onReady}
              onAvailablePaymentMethodsChange={onAvailablePaymentMethodsChange}
              onLoadError={onLoadError}
              // The probe is never clickable, so nothing to confirm.
              onConfirm={() => undefined}
            />
          </Elements>
        </div>
      )}
    </PaymentRequestContext.Provider>
  );
};
